import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';

import dayjs from 'dayjs';

import {
  Button,
  CircularProgress,
  formatError,
  useLocalStorage,
  windowExists,
} from '@classity-edtech/ui-base';
import { webPaths } from '../../routes/paths';
import { Container } from '@mui/system';
import Navigation from '../../components/Navigation';
import { Link, Stack, Typography } from '@mui/material';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import {
  ArrowLeftIcon,
  CardIcon,
  ChatIcon,
  Lock,
  TwoArrowsIcon,
} from '@classity-edtech/ui-base/dist/components/Displays';
import {
  useCreatePaymentMutation,
  useMyOrdersQuery,
  useOpayBankTransferMutation,
  useOpayCheckoutMutation,
} from '../../services/payment';
import {
  CurrencyCode,
  LanguageCode,
  OrderFullfillmentStatus,
  PaymentMethod,
  PaymentServiceProvider,
} from '@classity-edtech/platform-utils';
import { refinePricesUtil } from '../../utils/refinePrices';
import { closePaymentModal, useFlutterwave } from 'flutterwave-react-v3';
import { updateFlashMessage } from '../../store/actions/flashMessage';
import { useAppDispatch } from '../../store/hooks';
import Helmet from '../../components/Helmet';
import { useGetMyProfileQuery } from '../../services/users';

export interface HomeProps extends MuiBoxProps {}

export const Checkout = ({ className }: HomeProps) => {
  const dispatch: any = useAppDispatch();
  const defaultLanguage = LanguageCode.EN;
  const defaultCurrency = CurrencyCode.NG;
  const screens = useAppMediaQuery();
  const navigate = useNavigate();
  const smallScreens = screens.isMobile || screens.isTablet;
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [openPaymentMethod, setOpenPaymentMethod] = useState(false);
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);
  const [pendingTransfer, setPendingTransfer] = useLocalStorage('pendingTransfer', null);

  const {
    data: user,
    isLoading: loadingUser,
    isUninitialized: uninitialisedUser,
  } = useGetMyProfileQuery();

  const { data: myOrders, isLoading: loadingMyOrders } = useMyOrdersQuery({
    page: 1,
    limit: 1,
    status: OrderFullfillmentStatus?.PLACED,
  });

  const [createPayment] = useCreatePaymentMutation();
  const [opayBankTransfer] = useOpayBankTransferMutation();
  const [opayCheckout] = useOpayCheckoutMutation();

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: defaultCurrency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const getProductsAndPrices = () => {
    if (myOrders?.length) {
      const items = myOrders?.[0]?.items;
      const groupOf3Item = items?.find(t => t?.products?.some(p => p?.maxAttendees === 4));
      const groupOf6Item = items?.find(t => t?.products?.some(p => p?.maxAttendees === 7));
      const groupOf3Product = groupOf3Item?.products?.[0];
      const groupOf6Product = groupOf6Item?.products?.[0];
      const groupOf3Price = groupOf3Item?.prices?.[0];
      const groupOf6Price = groupOf6Item?.prices?.[0];

      return {
        groupof3: !groupOf3Product
          ? undefined
          : {
              price: groupOf3Price,
              product: groupOf3Product,
            },
        groupof6: !groupOf6Product
          ? undefined
          : {
              price: groupOf6Price,
              product: groupOf6Product,
            },
      };
    }
  };

  const checkoutTotal =
    (getProductsAndPrices()?.groupof3?.price?.currencies?.find(c => c?.code === defaultCurrency)
      ?.amount || 0) +
    (getProductsAndPrices()?.groupof6?.price?.currencies?.find(c => c?.code === defaultCurrency)
      ?.amount || 0);

  const paymentMethods = [
    {
      label: 'Pay with Card',
      icon: <CardIcon sx={{ width: '24px', height: '24px' }} />,
      value: 'card',
    },
    {
      label: 'Pay with Bank Transfer',
      icon: <TwoArrowsIcon sx={{ width: '24px', height: '24px' }} />,
      value: 'bank_transfer',
    },
  ];

  const togglePaymentMethod = () => {
    setOpenPaymentMethod(value => !value);
  };

  const config = ({
    payment_options,
    paymentId,
  }: {
    payment_options: string;
    paymentId: string;
  }) => ({
    public_key: (windowExists.__UI_CONFIG__ as any).flutterwave.publicKey,
    tx_ref: paymentId,
    amount: myOrders?.[0]?.totalAmount,
    currency: myOrders?.[0]?.currencyCode,
    payment_options,
    customer: {
      email: user?.email,
      phone_number: user?.phone,
      name: `${user?.profile?.firstname} ${user?.profile?.lastname}`,
    },
    customizations: {
      title: 'Classity',
      description: 'Classity classes payment',
      logo: '',
    },
  });

  const handlePaymentCreation = async method => {
    setIsLoadingPayment(true);

    const createPaymentResultData = await createPayment({
      description: [
        {
          language: 'en',
          text: 'Product event payment',
        },
      ],
      orderId: myOrders?.[0]?.id,
      paymentServiceProvider: {
        name: method === 'card' ? PaymentServiceProvider.FLUTTERWAVE : PaymentServiceProvider.OPAY,
        paymentMethod:
          method === 'bank_transfer' ? PaymentMethod.BANK_TRANSFER : PaymentMethod.CARD,
      },
    });

    if ('error' in createPaymentResultData) {
      const error = formatError(createPaymentResultData.error.message);

      setIsLoadingPayment(false);

      return dispatch(
        updateFlashMessage({
          open: true,
          severity: 'error',
          message: 'An error occurred!',
        }),
      );
    }

    if ('data' in createPaymentResultData) {
      if (method === 'card') {
        useFlutterwave(
          config({ payment_options: method, paymentId: createPaymentResultData?.data?.id }),
        )({
          callback: response => {
            closePaymentModal();
            setIsLoadingPayment(false);
            navigate(
              `/${webPaths.orderConfirmation}?paymentId=${createPaymentResultData?.data?.id}&transactionId=${response?.flw_ref}`,
            );
          },
          onClose: () => {
            setIsLoadingPayment(false);
          },
        });
      }

      if (method === 'bank_transfer') {
        if (pendingTransfer?.expiresAt && dayjs(pendingTransfer?.expiresAt).isAfter(dayjs())) {
          return navigate(pendingTransfer?.url);
        }

        const opayBankTransferResultData = await opayBankTransfer({
          amount: {
            total: myOrders?.[0]?.totalAmount * 100,
            currency: myOrders?.[0]?.currencyCode,
          },
          payMethod: 'BankTransfer',
          country: 'NG',
          product: {
            description: 'Classity tuition',
            name: 'Tuition fee',
          },
          reference: createPaymentResultData?.data?.id,
          customerName: `${user?.profile?.firstname} ${user?.profile?.lastname}`,
          userInfo: {
            userEmail: user?.email,
            userId: user?.id,
            userMobile: user?.phone,
            userName: `${user?.profile?.firstname} ${user?.profile?.lastname}`,
          },
          userPhone: user?.phone,
        });

        if ('error' in opayBankTransferResultData) {
          const error = formatError(opayBankTransferResultData.error.message);

          setIsLoadingPayment(false);

          return dispatch(
            updateFlashMessage({
              open: true,
              severity: 'error',
              message: 'An error occurred!',
            }),
          );
        }

        if ('data' in opayBankTransferResultData) {
          const data = opayBankTransferResultData.data?.data;

          const currencyFormatter = new Intl.NumberFormat(data?.amount?.currency, {
            style: 'currency',
            currency: data?.amount?.currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          });

          const url = `/${webPaths.bankTranserConfirmation}?reference=${data?.reference}&bankName=${data?.nextAction?.transferBankName}&bankAccount=${data?.nextAction?.transferAccountNumber}&amount=${currencyFormatter.format(data?.amount?.total / 100)}&expiredTimestamp=${data?.nextAction?.expiredTimestamp}&transactionId=${data?.orderNo}`;

          setPendingTransfer({
            url,
            expiresAt: data?.nextAction?.expiredTimestamp * 1000,
          });

          return navigate(url);
        }
      }

      if (method === 'payment_link') {
        const returnURLHost =
          process?.env?.NODE_ENV === 'development'
            ? 'https://localhost:4002'
            : 'https://classity.co';

        const paymentInput = {
          reference: createPaymentResultData?.data?.id,
          amount: {
            total: myOrders?.[0]?.totalAmount * 100,
            currency: myOrders?.[0]?.currencyCode,
          },
          displayName: 'Classity EdTech',
          product: {
            description: 'Classity tuition',
            name: 'Tuition fee',
          },
          country: 'NG',
          // TODO: Order confirmation will be displayed on the opay checkout page
          // returnUrl: `${returnURLHost}/${webPaths.orderConfirmation}`,
          customerName: `${user?.profile?.firstname} ${user?.profile?.lastname}`,
          userInfo: {
            userEmail: user?.email,
            userId: user?.id,
            userMobile: user?.phone,
            userName: `${user?.profile?.firstname} ${user?.profile?.lastname}`,
          },
          evokeOpay: true,
          customerVisitSource: 'BROWSER',
        };

        const opayCheckoutResultData = await opayCheckout(paymentInput);

        if ('error' in opayCheckoutResultData) {
          const error = formatError(opayCheckoutResultData.error.message);

          setIsLoadingPayment(false);

          return dispatch(
            updateFlashMessage({
              open: true,
              severity: 'error',
              message: 'An error occurred!',
            }),
          );
        }

        if ('data' in opayCheckoutResultData) {
          const data = opayCheckoutResultData.data?.data;

          const currencyFormatter = new Intl.NumberFormat(
            data?.amount?.currency || CurrencyCode.NG,
            {
              style: 'currency',
              currency: data?.amount?.currency || CurrencyCode.NG,
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            },
          );

          return navigate(
            `/${webPaths.paymentLink}?reference=${data?.reference}&cashierUrl=${encodeURI(data?.cashierUrl)}&amount=${currencyFormatter.format(data?.amount?.total / 100)}&transactionId=${data?.orderNo}`,
          );
        }
      }
    }
  };

  const handlePaymentMethod = ({ method, isDektop }: { method: string; isDektop?: boolean }) => {
    setPaymentMethod(method);
    setOpenPaymentMethod(false);

    if (isDektop) {
      handlePaymentCreation(method);
    }
  };

  const handleMobilePaymentMethod = payMethod => {
    handlePaymentCreation(payMethod || paymentMethod);
  };

  const formatEventDate = ({ start, end }: { start: string; end: string }) => {
    const startTime = dayjs(start);
    const endTime = dayjs(end);

    return `${startTime.format('ddd, DD MMM YYYY @h:mma')} - ${endTime.format('h:mma')}`;
  };

  const handleGoBack = () => {
    navigate(`/${webPaths.studentBookTuition}`);
  };

  useEffect(() => {
    if (!user && !loadingUser && !uninitialisedUser) {
      localStorage.removeItem('token');
      navigate(`${webPaths.root}`, { replace: true });
    }
  }, [user]);

  return (
    <>
      <Helmet />
      <Container sx={{ pb: { xs: 6, md: 3 } }}>
        <Navigation goBack={handleGoBack} closeAction={() => {}} currentStepIndex={1} hideInfo />
        {isLoadingPayment && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 60,
              background: '#000000bb',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {loadingMyOrders || loadingUser ? (
          <Box
            sx={{
              width: '100%',
              height: '80vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ width: { xs: '100%', md: '568px' }, my: { xs: 3, md: 6 }, mx: 'auto' }}>
            <Typography
              variant={smallScreens ? 'subtitle5' : 'subtitle3'}
              sx={{ textAlign: { md: 'center' }, display: 'block', mx: 'auto' }}
            >
              Checkout
            </Typography>
            <Typography
              variant={smallScreens ? 'body3' : 'body2'}
              sx={{
                maxWidth: '500px',
                textAlign: { md: 'center' },
                display: 'block',
                mb: 4,
                mx: 'auto',
              }}
            >
              Complete payment to confirm booking. Your booking is currently reserved for a limited
              time.
            </Typography>
            <Stack sx={{ mt: { xs: 3, md: 4 }, gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant='subtitle4'>Your Order</Typography>
                <Typography variant='subtitle5'>Subtotal</Typography>
              </Box>
              {getProductsAndPrices()?.groupof3 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Stack>
                    <Typography variant='body3SemiBold'>
                      {
                        getProductsAndPrices()?.groupof3?.product?.name?.find(
                          n => n?.language === defaultLanguage,
                        )?.displayName
                      }
                    </Typography>
                    <Typography variant='body3'>
                      {
                        getProductsAndPrices()?.groupof3?.product?.description?.find(
                          n => n?.language === defaultLanguage,
                        )?.text
                      }
                    </Typography>
                    <Typography variant='body3'>
                      {formatEventDate({
                        end: getProductsAndPrices()?.groupof3?.product?.endTime?.dateTime,
                        start: getProductsAndPrices()?.groupof3?.product?.startTime?.dateTime,
                      })}
                    </Typography>
                  </Stack>
                  <Typography variant='body3SemiBold'>
                    {refinePricesUtil({
                      formattedAmount: currencyFormatter.format(
                        getProductsAndPrices()?.groupof3?.price?.currencies?.find(
                          c => c?.code === defaultCurrency,
                        )?.amount || 0,
                      ),
                      currency: defaultCurrency,
                    })}
                  </Typography>
                </Box>
              )}
              {getProductsAndPrices()?.groupof6 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Stack>
                    <Typography variant='body3SemiBold'>
                      {
                        getProductsAndPrices()?.groupof6?.product?.name?.find(
                          n => n?.language === defaultLanguage,
                        )?.displayName
                      }
                    </Typography>
                    <Typography variant='body3'>
                      {
                        getProductsAndPrices()?.groupof6?.product?.description?.find(
                          n => n?.language === defaultLanguage,
                        )?.text
                      }
                    </Typography>
                    <Typography variant='body3'>
                      {formatEventDate({
                        end: getProductsAndPrices()?.groupof6?.product?.endTime?.dateTime,
                        start: getProductsAndPrices()?.groupof6?.product?.startTime?.dateTime,
                      })}
                    </Typography>
                  </Stack>
                  <Typography variant='body3SemiBold'>
                    {refinePricesUtil({
                      formattedAmount: currencyFormatter.format(
                        getProductsAndPrices()?.groupof6?.price?.currencies?.find(
                          c => c?.code === defaultCurrency,
                        )?.amount || 0,
                      ),
                      currency: defaultCurrency,
                    })}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant='subtitle4' sx={{ width: '100%' }}>
                  Total to pay
                </Typography>
                <Typography
                  variant='body2SemiBold'
                  sx={{
                    borderTop: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
                    width: '100%',
                    textAlign: 'right',
                  }}
                >
                  {refinePricesUtil({
                    formattedAmount: currencyFormatter.format(checkoutTotal || 0),
                    currency: defaultCurrency,
                  })}
                </Typography>
              </Box>
            </Stack>
            <Box
              sx={{
                mt: { xs: 2, md: 3 },
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              }}
            >
              <Box
                sx={{
                  display: { xs: 'block', md: 'none' },
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    border: `solid 1px ${globalColours['$global-colour-interactive-control-default']}`,
                    borderRadius: '16px',
                    cursor: 'pointer',
                  }}
                  onClick={togglePaymentMethod}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <Box
                      sx={{
                        width: '48px',
                        height: '48px',
                        minWidth: '48px',
                        minHeight: '48px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: globalColours['$global-colour-sentiment-info-default'],
                        color: globalColours['$global-colour-base-contrast'],
                      }}
                    >
                      {paymentMethods?.find(p => p.value === paymentMethod)?.icon}
                    </Box>
                    <Typography variant='body3'>
                      {paymentMethods?.find(p => p.value === paymentMethod)?.label}
                    </Typography>
                  </Box>
                  <ArrowLeftIcon
                    sx={{ width: '24px', height: '24px', transform: 'rotate(-90deg)' }}
                  />
                </Box>

                {openPaymentMethod && (
                  <Box
                    sx={{
                      borderRadius: '16px',
                      background: globalColours['$global-colour-background-screen-default'],
                      width: '100%',
                      zIndex: 5,
                      boxShadow: '0px 0px 40px 0px rgba(69, 71, 69, 0.20)',
                      position: 'absolute',
                      top: 'calc(100% + 8px)',
                    }}
                  >
                    {paymentMethods?.map(p => (
                      <Box
                        key={p.value}
                        sx={{ px: 2, py: 1.5, cursor: 'pointer' }}
                        onClick={() => handlePaymentMethod({ method: p.value })}
                      >
                        <Typography variant='body2'>{p.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {openPaymentMethod && (
                  <Box
                    sx={{ position: 'fixed', inset: 0, background: 'transparent', zIndex: 2 }}
                    onClick={togglePaymentMethod}
                  ></Box>
                )}
              </Box>

              {paymentMethods?.map(p => (
                <Box
                  key={p.value}
                  sx={{
                    p: 2,
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    border:
                      p.value === paymentMethod
                        ? `solid 1px ${globalColours['$global-colour-interactive-control-default']}`
                        : `solid 1px ${globalColours['$global-colour-border-neutral']}`,
                    borderRadius: '16px',
                    cursor: 'pointer',
                  }}
                  onClick={() => handlePaymentMethod({ method: p.value, isDektop: true })}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <Box
                      sx={{
                        width: '48px',
                        height: '48px',
                        minWidth: '48px',
                        minHeight: '48px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: globalColours['$global-colour-sentiment-info-default'],
                        color: globalColours['$global-colour-base-contrast'],
                      }}
                    >
                      {p.icon}
                    </Box>
                    <Typography variant='body3'>{p.label}</Typography>
                  </Box>
                  <ArrowLeftIcon
                    sx={{ width: '24px', height: '24px', transform: 'rotate(180deg)' }}
                  />
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                p: 2,
                mt: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                border: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
                borderRadius: '16px',
                cursor: 'pointer',
                width: '100%',
              }}
              onClick={() =>
                smallScreens
                  ? handleMobilePaymentMethod('payment_link')
                  : handlePaymentMethod({ method: 'payment_link', isDektop: true })
              }
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <Box
                  sx={{
                    width: '48px',
                    height: '48px',
                    minWidth: '48px',
                    minHeight: '48px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: globalColours['$global-colour-sentiment-info-default'],
                    color: globalColours['$global-colour-base-contrast'],
                  }}
                >
                  <ChatIcon sx={{ width: '24px', height: '24px' }} />
                </Box>
                <Stack>
                  <Typography variant='body3SemiBold'>Share payment link</Typography>
                  <Typography variant='body3'>Request payment from family or friend</Typography>
                </Stack>
              </Box>
              <ArrowLeftIcon sx={{ width: '24px', height: '24px', transform: 'rotate(180deg)' }} />
            </Box>
            <Typography
              variant='body3'
              sx={{
                color: globalColours['$global-colour-content-secondary'],
                mt: { xs: 3, md: 4 },
                mb: { xs: 2, md: 4 },
                display: 'block',
                textAlign: 'center',
              }}
            >
              By completing the payment, you are agreeing to our{' '}
              <Link
                href='#'
                sx={{
                  fontWeight: '600',
                  color: globalColours['$global-colour-content-secondary'],
                  textDecorationColor: globalColours['$global-colour-content-secondary'],
                }}
              >
                Cancellation Policy
              </Link>{' '}
              and{' '}
              <Link
                href='#'
                sx={{
                  fontWeight: '600',
                  color: globalColours['$global-colour-content-secondary'],
                  textDecorationColor: globalColours['$global-colour-content-secondary'],
                }}
              >
                Refund Policy
              </Link>
              .
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Lock
                sx={{
                  width: '24px',
                  height: '24px',
                  color: globalColours['$global-colour-core-brand-1'],
                }}
              />
              <Typography variant='body3SemiBold'>Secure Checkout - SSL Encrypted</Typography>
            </Box>
            <Typography variant='body3'>
              We ensure your financial and personal details are secure during every transaction.
            </Typography>
            <Button
              sx={{
                py: 1.5,
                px: 3,
                height: smallScreens ? '64px' : '48px',
                zIndex: 20,
                display: { xs: 'flex', md: 'none' },
              }}
              isLoading={false}
              onClick={() => handleMobilePaymentMethod('')}
              showRightIcon={smallScreens ? true : false}
            >
              <Typography variant='body3' sx={{ textTransform: 'uppercase' }}>
                {refinePricesUtil({
                  formattedAmount: currencyFormatter.format(checkoutTotal || 0),
                  currency: defaultCurrency,
                })}
                - <Typography variant='body3SemiBold'>PAY</Typography>
              </Typography>
            </Button>
          </Box>
        )}
      </Container>
    </>
  );
};

Checkout.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

Checkout.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(Checkout)(
  ({ theme }) => `
`,
);
